
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import HorizontalTitleListGraphql from '@/components/timeline/HorizontalTitleListGraphql.vue';
import SponsoredRecommendation from '@/components/sponsored-recommendations/SponsoredRecommendation.vue';

import { ImpressionTrackingEvents } from '@/enums/events';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';

import type { SimilarTitle, TitleDetailNodeSeason, TitleDetailsNode } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragmentFragment } from '@/pages/graphql/queries/GetPopularTitles.query';

@Component<SimilarTitlesGraphql>({
	name: 'SimilarTitlesGraphql',
	components: { HorizontalTitleListGraphql, SponsoredRecommendation },
})
export default class SimilarTitlesGraphql extends Vue {
	@Prop({ required: true }) titles: SimilarTitle[];
	@Prop({ required: true }) title: NonNullable<TitleDetailsNode | TitleDetailNodeSeason['show']>;
	@Prop({ default: null }) sponsoredAd: SponsoredAdFragmentFragment | null;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ default: '' }) impressionTrackingLabel: 'upcoming' | 'upcoming_genre';

	@Watch('title.id')
	onTitleIdChange(newTitleId: string, oldTitleId: string) {
		if (process.client && newTitleId !== oldTitleId) {
			this.trackSimilarTitlesLoaded();
		}
	}

	get impressionTrackingCategory() {
		return ImpressionTrackingEvents.SIMILAR_TITLES;
	}

	created() {
		// Note(valerio.mazza): SSR special case we need to track the Event on created
		if (process.server && this.titles?.length) {
			this.trackSimilarTitlesLoaded();
		}
	}

	trackSimilarTitlesLoaded() {
		const properties: TrackingProviderPropertiesInterface = { action: 'loaded', nonInteraction: true };
		if (this.impressionTrackingLabel) properties.label = this.impressionTrackingLabel;

		TrackingHelper.trackEvent(ImpressionTrackingEvents.SIMILAR_TITLES, properties, this.additionalContexts);
	}

	trackSimilarTitlesClicked({ index, title }: { index: number; title: SimilarTitle }) {
		let property: string = ImpressionTrackingEvents.SIMILAR_TITLES;
		if (this.impressionTrackingLabel === 'upcoming') property = 'similartitles_upcoming';
		if (this.impressionTrackingLabel === 'upcoming_genre') property = 'similartitles_upcoming_genre';

		TrackingHelper.trackEvent('userinteraction', {
			action: 'title_clicked',
			label: `${this.title.id},${title.id ?? ''}`,
			value: index,
			property,
		});
	}

	trackScroll(label: string) {
		const properties: TrackingProviderPropertiesInterface = { action: 'horizontal_scroller_clicked', label };
		if (this.impressionTrackingLabel === 'upcoming') properties.property = 'upcoming';
		if (this.impressionTrackingLabel === 'upcoming_genre') properties.property = 'upcoming_genre';

		TrackingHelper.trackEvent('userinteraction', properties, this.additionalContexts);
	}
}
